import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Service, { inject } from '@ember/service';
import { task, timeout, didCancel } from 'ember-concurrency';
import { get, action, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { isEqual } from '@ember/utils';
import { beebeeAjaxGet, bbParam } from '../lib/utility/ajax.js';
import moment from 'moment-timezone';
import 'ember-inflector';
import '../lib/utility/province.js';
import { resetPageOnFilterChange } from '../lib/index-page-reset.js';
import ListSwitch from '../lib/index-list-switch.js';
import getConfig from '../lib/get-config.js';

var _dec, _dec2, _dec3, _dec4, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14;
// ============================================================================
// COSTANTI
// ============================================================================

/** Ritardo debounce per ricerche (ms) */
const DEBOUNCE_MS = 500;

/** Formato data per le query */
const DATE_FORMAT = 'YYYY-MM-DD HH:mm';

/** Formato data per visualizzazione periodo precedente */
const DATE_DISPLAY_FORMAT = 'DD MMM YYYY';

/** Stato di default per l'indice */
const DEFAULT_STATUS = 'index';

/**
 * Document types whose list loads the SdI deadlines (/scadenze_sdi). Invoice only:
 * DB function invoices_scadenze_sdi counts document_type = 'invoice' and nothing
 * else, so on the autofattura list it returned the INVOICE deadlines. Add
 * 'autofattura' back once the DB function counts self-invoices (POP-445).
 * Keep in sync with invoice-list showSdiDeadlines.
 */
const SDI_DOCUMENT_TYPES = ['invoice'];

/**
 * Document types whose list offers the "only credit notes" filter. Their URL
 * carries it as `credit_note`; the API still reads it from `success`, the same
 * wire name the payments list uses for "esito riuscito" (see _buildQuery).
 */
const CREDIT_NOTE_DOCUMENT_TYPES = new Set(['invoice', 'receipt', 'expense']);

/** Mapping URL stats per tipo documento */
const STATS_URL_MAP = {
  autofattura: 'autofatture_stats'
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parsa un oggetto di statistiche convertendo i valori in numeri
 * @param {Object} stats - Statistiche da parsare
 * @returns {Object} Statistiche con valori numerici
 */
function parseStats(stats) {
  const parsedStats = {};
  for (const key in stats) {
    if (Object.prototype.hasOwnProperty.call(stats, key)) {
      parsedStats[key] = parseFloat(stats[key]) || 0;
    }
  }
  return parsedStats;
}

/**
 * Calcola la variazione percentuale tra due valori
 * @param {number} current - Valore corrente
 * @param {number} previous - Valore precedente
 * @returns {Object} Oggetto con change, className e icon
 */
function calculateChange(current, previous) {
  if (previous === 0) {
    if (current === 0) {
      return {
        change: '+0.00',
        className: 'text-neutral',
        icon: 'arrow-right'
      };
    }
    return {
      change: '+100',
      className: 'text-success',
      icon: 'arrow-up-right'
    };
  }
  if (!previous) {
    return {
      change: null,
      className: '',
      icon: ''
    };
  }
  const percentChange = (current - previous) / previous * 100;
  if (percentChange > 0) {
    return {
      change: `+${percentChange.toFixed(2)}`,
      className: 'text-success',
      icon: 'arrow-up-right'
    };
  } else if (percentChange < 0) {
    return {
      change: `${percentChange.toFixed(2)}`,
      className: 'text-alert',
      icon: 'arrow-down-right'
    };
  }
  return {
    change: '+0.00',
    className: 'text-neutral',
    icon: 'arrow-right'
  };
}

/**
 * Arricchisce le statistiche con campi calcolati
 * @param {Object} meta - Metadati delle statistiche
 * @returns {Object} Meta arricchiti
 */
function enrichStatsMeta(meta = {}) {
  const enriched = {
    ...meta
  };
  enriched.totInOutPayment = (meta.tot_in ?? 0) - (meta.tot_out ?? 0);
  enriched.numInOutPayment = (meta.num_in ?? 0) + (meta.num_out ?? 0);
  enriched.totInvoice = (meta.tot_unpaied ?? 0) + (meta.tot_done ?? 0);
  enriched.totNumInvoice = (meta.num_unpaied ?? 0) + (meta.num_done ?? 0);
  return enriched;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

/**
 * Servizio per la gestione del contesto delle liste indicizzate.
 * Gestisce ricerca, paginazione, statistiche e selezione multipla.
 *
 * @class IndexContextService
 * @extends Service
 */
let IndexContextService = (_dec = task(function* (forceReload = false) {
  if (!this.controller?.applicationController) {
    return;
  }
  const q = this.controller?.q;

  // Ignora query troppo corte (tranne numeri)
  if (q?.length > 0 && q.length < 3 && isNaN(q)) {
    return;
  }

  // Applica debounce se necessario
  const cacheIndex = this._cacheIndex;
  if (forceReload || !cacheIndex || !isEqual(cacheIndex.pageUrl, window.location.href)) {
    yield timeout(DEBOUNCE_MS);
  }
  this.searchItem.perform(forceReload);
}).restartable(), _dec2 = task(function* (forceReload = false, q = null) {
  if (!this.controller?.applicationController) {
    return;
  }

  // Aggiorna query se fornita
  if (q !== null && q !== undefined) {
    this.q = q;
  }
  const query = resetPageOnFilterChange(this, this._buildQuery()); // new q/filter → pag 1
  const cacheIndex = this._cacheIndex;

  // Salva selezioni correnti
  this.oldCheck = this.checkedItems;

  // Usa cache se disponibile e valida
  if (!forceReload && cacheIndex && isEqual(cacheIndex.pageUrl, window.location.href)) {
    this.items = cacheIndex.data;
    this._rebuildCheckedItems();
  } else if (this.externalSearchItemTask) {
    // Usa task esterno se configurato
    return yield this.externalSearchItemTask.perform(query);
  } else {
    // Esegue query standard
    yield* this._executeSearchQuery(query);
  }

  // Carica statistiche per documenti con tipo
  if (this.controller?.model?.documentType) {
    yield this.searchCustomStatus.perform();
  }
}).restartable(), _dec3 = task(function* () {
  try {
    const scadenze = yield beebeeAjaxGet(`${getConfig(this).APP.urlApi}/scadenze_sdi`, this.session.token, {
      contact_id: this.controller.from_id,
      year: this.controller.year
    });
    this.scadenzeSdI = scadenze;
  } catch (e) {
    if (didCancel(e)) return;
    this._handleError(e);
  }
}).restartable(), _dec4 = task(function* () {
  yield timeout(DEBOUNCE_MS);
  if (!this.controller?.applicationController) {
    return;
  }
  if (!this.controller?.model) {
    return;
  }
  const documentType = this.controller.model.documentType;
  const modelName = this.controller.model.modelName;
  const url = STATS_URL_MAP[documentType] || `${documentType}s_stats`;
  // Captured before any yield: the status can change (and the list cache be reset) while the requests run.
  const cacheKey = {
    modelName,
    status: this._currentStatus
  };
  const statsPath = `indexLoaded.${modelName}.${cacheKey.status}.stats`;

  // Carica scadenze SdI per invoice/autofattura
  if (SDI_DOCUMENT_TYPES.includes(documentType)) {
    yield this.loadScadenzeSdiTask.perform();
  } else {
    this.scadenzeSdI = null;
  }

  // Usa cache se disponibile
  const cachedStats = get(this.controller.applicationController, statsPath);
  if (cachedStats) {
    this.stats = cachedStats;
    return;
  }
  const getParams = this._buildQuery();

  // Validazione date
  if (!this._areDatesValid(getParams.from_date, getParams.to_date)) {
    return;
  }

  // Compute previous-year date range before any yield (values come from
  // controller state, not from the current-stats response).
  const canCompare = this._canCompareWithPreviousPeriod(getParams);
  const prevPeriod = canCompare ? this._computePreviousPeriod() : null;
  try {
    // Fire both requests in parallel when a comparison is possible.
    const [json, previousJson] = yield Promise.all([beebeeAjaxGet(`${getConfig(this).APP.urlApi}/${url}`, this.session.token, getParams), prevPeriod ? beebeeAjaxGet(`${getConfig(this).APP.urlApi}/${url}`, this.session.token, this._buildQuery(prevPeriod.fromDate, prevPeriod.toDate)) : Promise.resolve(null)]);
    if (!this.controller?.applicationController) {
      return;
    }
    const enrichedMeta = enrichStatsMeta(json.meta);
    this.stats = enrichedMeta;
    if (!canCompare || !previousJson) {
      this._clearPreviousStats();
      return;
    }
    this._applyPreviousYearComparison(previousJson, prevPeriod, cacheKey);
  } catch (e) {
    if (didCancel(e)) return;
    this._handleError(e);
  }
}).restartable(), _class = class IndexContextService extends Service {
  constructor(...args) {
    super(...args);
    // --------------------------------------------------------------------------
    // Service Injections
    // --------------------------------------------------------------------------
    _initializerDefineProperty(this, "store", _descriptor, this);
    _initializerDefineProperty(this, "router", _descriptor2, this);
    _initializerDefineProperty(this, "session", _descriptor3, this);
    _initializerDefineProperty(this, "log", _descriptor4, this);
    // --------------------------------------------------------------------------
    // Tracked Properties
    // --------------------------------------------------------------------------
    /** @type {Array} Lista degli elementi correnti */
    _initializerDefineProperty(this, "items", _descriptor5, this);
    /** @type {Object} Statistiche correnti */
    _initializerDefineProperty(this, "stats", _descriptor6, this);
    /** @type {Array} Elementi selezionati dalla vista precedente */
    _initializerDefineProperty(this, "oldCheck", _descriptor7, this);
    /** @type {Array} Filtri custom fields */
    _initializerDefineProperty(this, "cfsFilter", _descriptor8, this);
    /** @type {Object|null} Scadenze SdI */
    _initializerDefineProperty(this, "scadenzeSdI", _descriptor9, this);
    /** @type {Object|null} Controller corrente */
    _initializerDefineProperty(this, "controller", _descriptor0, this);
    /** @type {Array} Elementi attualmente selezionati */
    _initializerDefineProperty(this, "checkedItems", _descriptor1, this);
    /**
     * Keyboard-focused row of the list (BbList up/down/enter). -2 = none.
     * Tracked because every list row template reads
     * `(is-equal this.indexContext.focusIndex index)`: as an undeclared plain
     * property the arrow keys moved the value but never re-rendered the
     * highlight.
     * @type {number}
     */
    _initializerDefineProperty(this, "focusIndex", _descriptor10, this);
    // --------------------------------------------------------------------------
    // Regular Properties
    // --------------------------------------------------------------------------
    /** @type {Function|null} Handler per cambio route */
    this._routeDidChangeHandler = null;
    /** @type {Task|null} Task esterno per ricerca */
    this.externalSearchItemTask = null;
    this.listSwitch = new ListSwitch();
    // --------------------------------------------------------------------------
    // Tasks
    // --------------------------------------------------------------------------
    /**
     * Task con debounce per aggiornamento indice
     * Evita ricerche troppo frequenti
     */
    _initializerDefineProperty(this, "updateIndexDebounce", _descriptor11, this);
    /**
     * Task principale per ricerca elementi
     * Gestisce cache, query esterne e statistiche
     */
    _initializerDefineProperty(this, "searchItem", _descriptor12, this);
    /**
     * Task per caricamento scadenze SdI
     */
    _initializerDefineProperty(this, "loadScadenzeSdiTask", _descriptor13, this);
    /**
     * Task per ricerca statistiche personalizzate
     * Carica statistiche correnti e confronto con anno precedente
     */
    _initializerDefineProperty(this, "searchCustomStatus", _descriptor14, this);
  }
  // UI nuova: a different list never shows the previous one's rows

  // --------------------------------------------------------------------------
  // Getters & Setters
  // --------------------------------------------------------------------------

  /**
   * Metadata degli elementi correnti
   * @type {Object|undefined}
   */
  get meta() {
    return this.items?.meta;
  }
  set meta(value) {
    if (this.items) {
      this.items.meta = value;
      // Riassegna per invalidare il tracking Glimmer sulla @tracked items
      this.items = this.items;
    }
  }

  /**
   * Indica se ci sono elementi selezionati
   * @type {boolean}
   */
  get isCheck() {
    return this.checkedItems.length > 0;
  }

  /**
   * Imposta lo stato checked di un elemento e notifica il cambio.
   * Da usare al posto di `(mut item.checked)` nei template.
   * @param {Object} item - L'elemento da spuntare/dèspuntare
   * @param {boolean} value - Nuovo valore
   */
  setItemChecked(item, value) {
    item.checked = value;
    this._rebuildCheckedItems();
  }

  /**
   * Notifica manualmente il cambio dello stato checked (es. dopo setEach).
   */
  notifyChecked() {
    this._rebuildCheckedItems();
  }

  /**
   * Resetta tutte le selezioni correnti e precedenti.
   * Da usare dopo operazioni bulk completate con successo.
   * NON usare su forceReload generico — la selezione cross-pagina va preservata.
   */
  resetSelections() {
    const currentItems = this.items || [];
    currentItems.forEach(item => {
      item.checked = false;
    });
    this.oldCheck = [];
    this.checkedItems = [];
  }

  /**
   * Ricostruisce l'array checkedItems dai dati correnti e da oldCheck.
   * @private
   */
  _rebuildCheckedItems() {
    const currentItems = this.items || [];
    const contentChecked = currentItems.filter(item => item.checked);
    const oldCheck = (this.oldCheck || []).filter(item => !currentItems.some(i => i.id === item.id && !i.checked));
    this.checkedItems = [...contentChecked, ...oldCheck].reduce((acc, item) => {
      if (!acc.some(i => i.id === item.id)) {
        acc.push(item);
      }
      return acc;
    }, []);
  }

  /**
   * IDs delle colonne custom fields configurate
   * @type {Array}
   */
  get customFieldsColumnsIds() {
    const modelName = this.controller?.model?.modelName;
    if (!modelName) return [];
    const optionField = this.session.option_field;
    return optionField?.custom_columns?.[`${modelName}s`]?.custom_fields || [];
  }

  /**
   * Status corrente o default
   * @type {string}
   * @private
   */
  get _currentStatus() {
    return this.controller?.status || DEFAULT_STATUS;
  }

  /**
   * Cache dell'indice per il model corrente
   * @type {Object|undefined}
   * @private
   */
  get _cacheIndex() {
    const modelName = this.controller?.model?.modelName;
    if (!modelName) return undefined;
    return this.controller.applicationController?.indexLoaded?.[modelName]?.[this._currentStatus];
  }

  // --------------------------------------------------------------------------
  // Lifecycle Methods
  // --------------------------------------------------------------------------

  /**
   * Attiva il servizio per una route lista
   * @param {Controller} controller - Controller della lista
   */
  activateForListRoute(controller) {
    this.controller = controller;
    this._migrateLegacyCreditNoteParam(controller);
    this.listSwitch.enter(this, controller);
    if (!this._routeDidChangeHandler) {
      this._routeDidChangeHandler = this._onRouteDidChange.bind(this);
      this.router.on('routeDidChange', this._routeDidChangeHandler);
    }
  }

  /**
   * Before `credit_note` existed, invoice/receipt/expense lists stored "only
   * credit notes" in `success`, the payments "esito riuscito" param. Saved
   * URLs and bookmarks still carry `?success=true` on those lists: move the
   * value to `credit_note` so the filter keeps working and shows as active.
   * @param {Controller} controller - Controller della lista
   * @private
   */
  _migrateLegacyCreditNoteParam(controller) {
    if (!CREDIT_NOTE_DOCUMENT_TYPES.has(controller?.model?.documentType)) return;
    if (!controller.success) return;
    controller.credit_note = controller.success;
    controller.success = null;
  }

  /**
   * Disattiva il servizio e rimuove gli event listener
   */
  deactivate() {
    if (this._routeDidChangeHandler) {
      this.router.off('routeDidChange', this._routeDidChangeHandler);
      this._routeDidChangeHandler = null;
    }
    this.updateIndexDebounce.cancelAll();
    this.searchItem.cancelAll();
    this.loadScadenzeSdiTask.cancelAll();
    this.searchCustomStatus.cancelAll();
    this.cfsFilter = [];
    this.scadenzeSdI = null;
    this.controller = null;
  }
  willDestroy() {
    this.deactivate();
    super.willDestroy();
  }

  /**
   * Imposta il controller corrente
   * @param {Controller} controller - Controller da impostare
   */
  setController(controller) {
    this.controller = controller;
  }

  /**
   * Imposta un task esterno per la ricerca
   * @param {Task|null} taskInstance - Task da utilizzare
   */
  setExternalSearchItemTask(taskInstance = null) {
    this.externalSearchItemTask = taskInstance;
  }

  // --------------------------------------------------------------------------
  // Event Handlers
  // --------------------------------------------------------------------------

  /**
   * Handler per cambio route
   * @param {Transition} transition - Transizione corrente
   * @private
   */
  _onRouteDidChange() {
    if (!this.controller) return;
    this.updateIndexDebounce.perform();
  }
  /**
   * Esegue la query di ricerca e aggiorna la cache
   * @param {Object} query - Parametri query
   * @private
   */
  *_executeSearchQuery(query) {
    try {
      const json = yield this.store.query(this.controller?.model?.modelName, query);
      this.items = json;
      this._rebuildCheckedItems();

      // Aggiorna cache solo se non ci sono filtri custom fields
      if (!Array.isArray(this.cfsFilter) || this.cfsFilter.length < 1) {
        this._updateIndexCache(json);
      }
    } catch (e) {
      if (didCancel(e)) return;
      this._handleError(e);
    }
  }

  /**
   * Aggiorna la cache dell'indice
   * @param {Array} json - Dati da cachare
   * @private
   */
  _updateIndexCache(json) {
    if (!this.controller?.model) return;
    const modelName = this.controller.model.modelName;
    const indexLoaded = this.controller.applicationController.indexLoaded || {};
    const modelIndexLoaded = indexLoaded[modelName] || {};
    set(modelIndexLoaded, 'list_ids', json.map(item => item.id));
    set(modelIndexLoaded, this._currentStatus, {
      data: json,
      pageUrl: window.location.href
    });
    set(indexLoaded, modelName, modelIndexLoaded);
    set(this.controller.applicationController, 'indexLoaded', indexLoaded);
  }
  get arrStats() {
    const stats = this.stats || {};
    const commonStats = [{
      name: 'total',
      status: null,
      total_euro: (stats.total_unpaied ?? 0) + (stats.total_done ?? 0),
      tot_euro: stats.totInvoice ?? 0,
      tot_number: stats.totNumInvoice ?? 0,
      tot_change: stats.statsComparison?.totInvoiceChange ?? 0,
      hide_total_euro: false
    }, {
      name: 'draft',
      status: 'draft',
      total_euro: stats.total_draft ?? 0,
      tot_euro: stats.tot_draft ?? 0,
      tot_number: stats.num_draft ?? 0,
      hide_tot_change: true
    }, {
      name: 'unpaied',
      status: 'unpaied',
      total_euro: stats.total_unpaied ?? 0,
      tot_euro: stats.tot_unpaied ?? 0,
      tot_number: stats.num_unpaied ?? 0,
      tot_hover: stats.da_saldare ?? 0,
      hide_tot_change: true,
      hover: true
    }, {
      name: 'paied',
      status: 'done',
      total_euro: stats.total_done ?? 0,
      tot_euro: stats.tot_done ?? 0,
      tot_number: stats.num_done ?? 0,
      hide_tot_change: true
    }];
    switch (this.controller?.model?.documentType) {
      case 'invoice':
        return commonStats.slice(0, 4);
      case 'receipt':
        // Receipts have no draft state: total, unpaied, paied.
        return commonStats.filter((_, index) => [0, 2, 3].includes(index));
      case 'estimate':
        return [...commonStats.slice(1, 2), {
          name: 'waiting',
          status: 'wait',
          total_euro: stats.total_wait,
          tot_euro: stats.tot_wait,
          tot_number: stats.num_wait,
          tot_change: stats.statsComparison?.tot_waitChange,
          hide_tot_change: true
        }, {
          name: 'approved',
          status: 'accepted',
          total_euro: stats.total_accepted,
          tot_euro: stats.tot_accepted,
          tot_number: stats.num_accepted,
          tot_change: stats.statsComparison?.total_acceptedChange,
          hide_tot_change: true
        }];
      case 'order':
        return [...commonStats.slice(1, 2), {
          name: 'sent',
          status: 'wait',
          total_euro: stats.total_wait,
          tot_euro: stats.tot_wait,
          tot_number: stats.num_wait,
          tot_change: stats.statsComparison?.total_waitChange,
          hide_tot_change: true
        }, {
          name: 'concluded',
          status: 'accepted',
          total_euro: stats.total_accepted,
          tot_euro: stats.tot_accepted,
          tot_number: stats.num_accepted,
          tot_change: stats.statsComparison?.total_acceptedChange,
          hide_tot_change: true
        }];
      case 'contract':
        return [{
          name: 'open',
          status: 'open',
          total_euro: stats.total_open,
          tot_euro: stats.tot_open,
          tot_number: stats.num_open,
          tot_change: stats.statsComparison?.tot_openChange,
          hide_tot_change: true
        }, {
          name: 'closed',
          status: 'closed',
          total_euro: stats.total_closed,
          tot_euro: stats.tot_closed,
          tot_number: stats.num_closed,
          tot_change: stats.statsComparison?.tot_closedChange,
          hide_tot_change: true
        }];
      case 'ddt':
        return [{
          name: 'active',
          status: 'active',
          total_euro: stats.total_active,
          tot_euro: stats.tot_active,
          tot_number: stats.num_active,
          hide_tot_change: true
        }, {
          name: 'passive',
          status: 'passive',
          total_euro: stats.total_passive,
          tot_euro: stats.tot_passive,
          tot_number: stats.num_passive,
          hide_tot_change: true
        }];
      case 'autofattura':
        return [{
          name: 'autofattura',
          status: null,
          total_euro: stats.total_done,
          tot_euro: stats.tot_done,
          tot_number: stats.num_done,
          tot_change: stats.statsComparison?.tot_doneChange
        }];
      default:
        return [];
    }
  }

  // --------------------------------------------------------------------------
  // Private Helper Methods
  // --------------------------------------------------------------------------

  /**
   * Verifica se le date sono valide
   * @param {string|null} fromDate - Data inizio
   * @param {string|null} toDate - Data fine
   * @returns {boolean} True se entrambe le date sono valide o null
   * @private
   */
  _areDatesValid(fromDate, toDate) {
    const isFromValid = !fromDate || moment(fromDate, DATE_FORMAT, true).isValid();
    const isToValid = !toDate || moment(toDate, DATE_FORMAT, true).isValid();
    return isFromValid && isToValid;
  }

  /**
   * Verifica se è possibile confrontare con il periodo precedente
   * @param {Object} params - Parametri query
   * @returns {boolean} True se il confronto è possibile
   * @private
   */
  _canCompareWithPreviousPeriod(params) {
    return params.from_date && params.to_date && moment(params.from_date, DATE_FORMAT, true).isValid() && moment(params.to_date, DATE_FORMAT, true).isValid();
  }

  /**
   * Pulisce le statistiche del periodo precedente
   * @private
   */
  _clearPreviousStats() {
    this.stats = {
      ...this.stats,
      previousStats: null,
      previousPeriod: null,
      statsComparison: null
    };
  }

  /**
   * Calcola il range di date per il periodo precedente (anno -1).
   * Non fa yield — valori derivati solo da controller state.
   * @returns {{ fromDate: moment, toDate: moment }}
   * @private
   */
  _computePreviousPeriod() {
    const currentFromDate = moment(this.controller.from_date);
    const currentToDate = moment(this.controller.to_date);
    const today = moment().endOf('day');
    const fromDate = currentFromDate.clone().subtract(1, 'years');
    let toDate = currentToDate.clone().subtract(1, 'years');
    if (toDate.isAfter(today) || currentToDate.isAfter(today)) {
      toDate = today.clone().subtract(1, 'years');
    }
    return {
      fromDate,
      toDate
    };
  }

  /**
   * Applica il confronto con l'anno precedente sulle statistiche correnti.
   * Chiamato dopo che entrambe le risposte API sono già disponibili.
   * @param {Object} previousJson - Risposta API anno precedente
   * @param {{ fromDate: moment, toDate: moment }} prevPeriod
   * @param {{ modelName: string, status: string }} cacheKey list cache entry, captured when the request started
   * @private
   */
  _applyPreviousYearComparison(previousJson, prevPeriod, cacheKey) {
    if (!this.controller?.applicationController || !this.controller.model) {
      return;
    }
    const previousMeta = enrichStatsMeta(previousJson.meta);
    const currentStats = parseStats(this.stats);
    const previousStats = parseStats(previousMeta);
    const statsComparison = {};
    for (const key in currentStats) {
      if (Object.prototype.hasOwnProperty.call(currentStats, key) && Object.prototype.hasOwnProperty.call(previousStats, key)) {
        statsComparison[`${key}Change`] = calculateChange(currentStats[key], previousStats[key]);
      }
    }
    this.stats = {
      ...this.stats,
      previousStats,
      previousPeriod: {
        fromDate: prevPeriod.fromDate.format(DATE_DISPLAY_FORMAT),
        toDate: prevPeriod.toDate.format(DATE_DISPLAY_FORMAT)
      },
      statsComparison
    };
    this._cacheStats(cacheKey, this.stats);
  }

  /**
   * Stores the stats on the list cache entry they were requested for. The entry
   * (`indexLoaded.<model>.<status>`) is created by the list load and reset to {}
   * by a save / socket update: when it is gone, the stats are not cached (they
   * would resurrect a stale entry). A path `set()` here threw «Property set
   * failed: object in path …indexLoaded.invoice.done could not be found».
   * @param {{ modelName: string, status: string }} cacheKey
   * @param {Object} stats
   * @private
   */
  _cacheStats(cacheKey, stats) {
    const entry = this.controller?.applicationController?.indexLoaded?.[cacheKey?.modelName]?.[cacheKey?.status];
    if (entry && typeof entry === 'object') {
      set(entry, 'stats', stats);
    }
  }

  /**
   * Gestisce gli errori in modo centralizzato
   * @param {Error} error - Errore da gestire
   * @private
   */
  _handleError(error) {
    if (this.controller?.applicationController) {
      this.controller.applicationController.findError(error);
    } else {
      this.log.error('applicationController undefined', error);
    }
  }

  /**
   * Costruisce la query per le richieste API
   * @param {moment|null} fromDate - Data inizio override
   * @param {moment|null} toDate - Data fine override
   * @returns {Object} Parametri query
   * @private
   */
  _buildQuery(fromDate = null, toDate = null) {
    const controller = this.controller;
    const from = fromDate || controller.from_date;
    const to = toDate || controller.to_date;
    const query = {
      document_type: controller?.model?.documentType,
      q: controller.q,
      sort: controller.sort,
      status: controller.status,
      sent: controller.sent,
      // One wire param, two meanings: "only credit notes" on invoice/receipt/
      // expense lists, "esito riuscito" on payments.
      success: CREDIT_NOTE_DOCUMENT_TYPES.has(controller?.model?.documentType) ? controller.credit_note ?? null : controller.success ?? null,
      direction: controller.direction,
      pag: controller.pag,
      from_date: from ? moment(from).format(DATE_FORMAT) : null,
      to_date: to ? moment(to).format(DATE_FORMAT) : null,
      category_id: controller.category_id,
      sub_category_id: controller.sub_category_id,
      contact_id: controller.contact_id,
      from_id: controller.from_id,
      product_id: controller.product_id,
      all_any: controller.all_any,
      gdpr: controller.gdpr,
      voucher: controller.voucher,
      sdi_status: controller.sdi_status,
      archivied: controller.archivied,
      custom_id: this.customFieldsColumnsIds,
      checks: this.checkedItems.map(i => i.id),
      sezionale: controller.sezionale,
      _t: Date.now()
    };
    if (this.cfsFilter?.length > 0) {
      query.custom = this.cfsFilter.join(',');
    }
    return query;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Esporta i dati correnti in formato CSV
   */
  csvExport() {
    const modelName = this.controller?.model?.modelName;
    if (!modelName) return;
    const queryString = bbParam(this._buildQuery());
    const url = `${getConfig(this).APP.urlApi}/${modelName}s_csv?access_token=${this.session.token}&${queryString}`;
    window.open(url, '_system');
  }

  /**
   * Imposta un valore nel controller
   * @param {string} key - Chiave da impostare
   * @param {*} value - Valore da assegnare
   */
  setContent(key, value) {
    this.controller.set(key, value);
  }

  /**
   * Resetta il campo di ricerca
   */
  resetSearchField() {
    this.controller.set('q', '');
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "router", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "log", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "items", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "stats", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return {};
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "oldCheck", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "cfsFilter", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "scadenzeSdI", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "controller", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "checkedItems", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "focusIndex", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return -2;
  }
}), _applyDecoratedDescriptor(_class.prototype, "setItemChecked", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setItemChecked"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetSelections", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetSelections"), _class.prototype), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "updateIndexDebounce", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "searchItem", [_dec2], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "loadScadenzeSdiTask", [_dec3], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "searchCustomStatus", [_dec4], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "csvExport", [action], Object.getOwnPropertyDescriptor(_class.prototype, "csvExport"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setContent", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setContent"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "resetSearchField", [action], Object.getOwnPropertyDescriptor(_class.prototype, "resetSearchField"), _class.prototype), _class);

export { CREDIT_NOTE_DOCUMENT_TYPES, IndexContextService as default };
//# sourceMappingURL=index-context.js.map
